/**
 * Allys Area Properties Component
 * 
 * Displays a grid of available properties in the area
 * with links to property details.
 */

'use client';

import Link from 'next/link';
import Image from 'next/image';
import { Bed, Bathtub, ArrowSquareOut, ArrowRight } from '@phosphor-icons/react';
import type { AreaPropertiesProps, AreaPageMessages } from '@routeroyal/theme-core';

export interface AllysAreaPropertiesProps extends AreaPropertiesProps {
  /**
   * Base path for property links
   * @default '/properties'
   */
  propertyBasePath?: string;
  
  /**
   * Translation messages
   */
  messages?: AreaPageMessages;
  
  /**
   * Current locale
   */
  locale?: string;
}

export function AllysAreaProperties({
  properties,
  areaName,
  viewAllLink,
  maxItems = 3,
  isLoading = false,
  propertyBasePath = '/properties',
  messages,
  locale = 'en',
  className = '',
}: AllysAreaPropertiesProps) {
  const displayProperties = properties.slice(0, maxItems);

  if (isLoading) {
    return (
      <div className={`py-32 bg-white ${className}`}>
        <div className="container mx-auto px-6 md:px-12">
          <div className="animate-pulse">
            <div className="h-4 bg-gray-100 rounded w-32 mb-4"></div>
            <div className="h-12 bg-gray-100 rounded w-96 mb-16"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
              {[1, 2, 3].map((i) => (
                <div key={i} className="group">
                  <div className="h-80 bg-gray-100 rounded-3xl mb-6"></div>
                  <div className="px-2 space-y-4">
                    <div className="h-6 bg-gray-100 rounded w-3/4"></div>
                    <div className="h-4 bg-gray-100 rounded w-1/2"></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!properties.length) {
    return (
      <div className={`py-32 bg-white ${className}`}>
        <div className="container mx-auto px-6 md:px-12 text-center">
          <span 
            className="text-[var(--color-primary)] text-xs font-bold tracking-[0.2em] uppercase mb-4 block font-inter"
          >
            {messages?.realEstate || 'Real Estate'}
          </span>
          <h2 className="font-serif text-3xl text-[var(--color-text)] mb-4">
            {messages?.exclusiveProperties || 'Exclusive Properties'}
          </h2>
          <p 
            className="text-gray-500 font-light font-inter"
          >
            Properties in {areaName} coming soon.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`py-32 bg-white transition-colors duration-500 ${className}`}>
      <div className="container mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-end mb-16 gap-6">
          <div className="max-w-xl">
            <span 
              className="text-[var(--color-primary)] text-xs font-bold tracking-[0.2em] uppercase mb-4 block font-inter"
            >
              {messages?.realEstate || 'Real Estate'}
            </span>
            <h2 className="font-serif text-4xl md:text-5xl text-[var(--color-text)] leading-tight">
              {messages?.exclusiveProperties || 'Exclusive Properties'} <br/>
              <span className="text-gray-400 italic">{messages?.availableNow || 'Available Now'}</span>
            </h2>
          </div>
          
          {viewAllLink && (
            <Link 
              href={viewAllLink}
              className="group flex items-center gap-2 text-[var(--color-text)] hover:text-[var(--color-primary)] transition-colors uppercase text-xs font-bold tracking-widest font-inter"
            >
              {messages?.viewCollection || 'View Collection'}
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" weight="bold" />
            </Link>
          )}
        </div>

        {/* Property Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
          {displayProperties.map((property, idx) => (
            <Link 
              key={property.id}
              href={`/${locale}${propertyBasePath}/${property.slug || property.id}`}
              className="group cursor-pointer block animate-fadeInUp"
              style={{ animationDelay: `${idx * 100}ms` }}
            >
              {/* Image Container */}
              <div className="relative overflow-hidden rounded-3xl mb-6 shadow-lg group-hover:shadow-xl transition-shadow duration-500">
                {/* Status Badge */}
                <div className="absolute top-6 left-6 bg-white/90 backdrop-blur-sm text-[var(--color-text)] text-xs font-bold px-4 py-2 rounded-full z-10 uppercase tracking-wider shadow-sm">
                  {messages?.forSale || 'For Sale'}
                </div>
                
                {/* Image */}
                <div className="relative h-80 overflow-hidden">
                  <Image 
                    src={property.image} 
                    alt={property.title}
                    fill
                    className="object-cover group-hover:scale-105 transition-transform duration-700 ease-in-out" 
                    sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                  />
                </div>
                
                {/* Overlay */}
                <div className="absolute inset-0 bg-black/10 group-hover:bg-transparent transition-colors duration-500"></div>
                
                {/* View Link */}
                <div className="absolute bottom-6 right-6 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-lg">
                    <ArrowSquareOut size={18} className="text-[var(--color-primary)]" weight="bold" />
                  </div>
                </div>
              </div>
              
              {/* Property Info */}
              <div className="px-2">
                <div className="flex justify-between items-baseline mb-3">
                  <h3 className="font-serif text-2xl text-[var(--color-text)] group-hover:text-[var(--color-primary)] transition-colors">
                    {property.title}
                  </h3>
                  <span className="text-lg font-medium text-[var(--color-primary)]">
                    {property.price}
                  </span>
                </div>
                
                {/* Property Stats */}
                <div 
                  className="flex gap-6 text-gray-500 text-sm font-light font-inter"
                >
                  {property.bedrooms !== undefined && (
                    <div className="flex items-center gap-2">
                      <Bed size={18} className="text-gray-300" weight="light" />
                      <span>{property.bedrooms}</span>
                      <span className="hidden sm:inline">{messages?.beds || 'Beds'}</span>
                    </div>
                  )}
                  {property.bathrooms !== undefined && (
                    <div className="flex items-center gap-2">
                      <Bathtub size={18} className="text-gray-300" weight="light" />
                      <span>{property.bathrooms}</span>
                      <span className="hidden sm:inline">{messages?.baths || 'Baths'}</span>
                    </div>
                  )}
                  {property.area !== undefined && (
                    <div className="flex items-center gap-2">
                      <span>{property.area.toLocaleString()}</span>
                      <span>{property.areaUnit || 'sqft'}</span>
                    </div>
                  )}
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* Animation Styles */}
      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-fadeInUp {
          animation: fadeInUp 0.6s ease-out forwards;
          opacity: 0;
        }
      `}</style>
    </div>
  );
}
